/**
 * Requirements
 * - Ability to select multiple places or areas.
 * - Ability to select only a single place or area.
 * - Ability to display a map, sometimes, with the designates places.
 * - Places must be passable by prop (for instance populated
 *    previous resources selections, or previous filtering)
 *
 *
 * Container fetches results.
 * - GeoSearchRow handles user input. Based on if it's a point or polygon it
 * displays either one or two fields. First field is a simple auto-complete.
 * Second field is a integer only distance field.
 * - Container binds to redux store for results from fetches
 *
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { memoize } from 'lodash';
import { useSelector } from 'react-redux';

import GeoSearchRowContainer from './GeoSearchRowContainer';
import GeoSearchRowContainerPublic from './GeoSearchRowContainerPublic';
import GoogleMaps from '../../GoogleMaps/GoogleMaps';
import * as styles from './styles.css';
import { areasByIds } from '../../../entities/Area/selectors';
import { withTranslation } from 'react-i18next';

const GeoSearchContainer = (props) => {
  const {
    showMap = true,
    isEditable = true,
    maxAllowedGeoSearches,
    isConsultantPortal = false,
    input,
    i18n
  } = props;

  useEffect(() => {
    if (typeof input.value === 'string') {
      input.onChange(new List());
    }
  }, [input]);

  useEffect(() => {
    if (typeof input.value === 'string') {
      input.onChange(new List());
    }
  }, [input.value]);

  const onChange = memoize(index =>
    value => input.onChange(input.value.setIn([index], value)),
  );

  const onBlur = memoize(index =>
    value => input.onBlur(input.value.setIn([index], value).filter(v => v !== null)),
  );

  const push = () => {
    input.onChange(input.value.push(null));
  };

  const remove = memoize(index => () => input.onChange(input.value.delete(index)));

  const pointMarkers = () => {
    if (!input.value) return [];
    return input.value
      .filter((v) => !!v)
      .filter(({ geoCode }) => !geoCode)
      .map((area) => ({
        ...area.toJS(),
        coordinates: (area && area.coordinates && area.coordinates.toJS) ? area.coordinates.toJS() : area.coordinates,
        type: (area && area.type) ? area.type : 'point',
        geoCode: (area && area.geoCode) ? area.geoCode : undefined,
      }));
  };

  const geoCodesIds = () => {
    if (!input.value) return [];
    return input.value
      .filter((v) => !!v)
      .filter(({ geoCode }) => !!geoCode)
      .map(({ geoCode }) => (geoCode));
  };

  const geoCodes = useSelector((state) => areasByIds(state, geoCodesIds()));

  const markers = useSelector((state) => {
    const value = input.value || [];
    const pointMarkers = value
      .filter((v) => !!v)
      .filter(({ geoCode }) => !geoCode)
      .map((area) => ({
        ...area.toJS(),
        coordinates: (area && area.coordinates && area.coordinates.toJS) ? area.coordinates.toJS() : area.coordinates,
        type: (area && area.type) ? area.type : 'point',
        geoCode: (area && area.geoCode) ? area.geoCode : undefined,
      }));
    return pointMarkers.concat(geoCodes).filter((v) => !!v).map(marker => marker.toJS ? marker.toJS() : marker);
  });

  return (
    <div className={styles.container}>
      {showMap && (
        <GoogleMaps
          markers={markers}
        />
      )}

      <div className={styles.searchInputsContainer}>
        {!isEditable
          && (!input.value || input.value.size === 0)
          && <span>{i18n.t("Inga områden angivna.")}</span>}

        {!isEditable
          && input.value
          && input.value.map(area => (
            <ul key={area.id}>
              <li>{area.name}{!!area.distance && `, ${area.distance} km`}</li>
            </ul>
          ))}

        {isEditable && !isConsultantPortal
          && input.value
          && input.value.push(null).map((area, index) => (
            <GeoSearchRowContainer
              key={index}
              input={{
                value: area,
                onChange: onChange(index),
                onBlur: onBlur(index),
              }}
              maxAllowedGeoSearches={maxAllowedGeoSearches}
              index={index}
              removeAction={remove(index)}
            />))}
        {isEditable && isConsultantPortal
          && input.value
          && input.value.push(null).map((area, index) => (
            <GeoSearchRowContainerPublic
              key={index}
              input={{
                value: area,
                onChange: onChange(index),
                onBlur: onBlur(index),
              }}
              maxAllowedGeoSearches={maxAllowedGeoSearches}
              index={index}
              removeAction={remove(index)}
            />))}
        {maxAllowedGeoSearches && (
          <div>{i18n.t("Det går maximalt att söka på")} {maxAllowedGeoSearches} {i18n.t("regioner.")}</div>
        )}
      </div>
    </div>
  );
};

GeoSearchContainer.propTypes = {
  showMap: PropTypes.bool,
  isConsultantPortal: PropTypes.bool, 
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.instanceOf(List),
      PropTypes.string,
    ]).isRequired, // List of AreaRecord
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
  }).isRequired,
  isEditable: PropTypes.bool,
  maxAllowedGeoSearches: PropTypes.number,
};

export default withTranslation()(GeoSearchContainer);
